import { darken, rgba } from 'polished';
import styled from 'styled-components';

import {
  ContentMarginLine,
  gridColorSelector
} from '@/components/v2/GridLines';
import { LayoutSubgrid } from '@/components/v2/Page/LayoutGrid';
import { styles as headingStyles } from '@/components/v2/UtilityCss/Heading';
import { styles as textStyles } from '@/components/v2/UtilityCss/Text';
import { generateFluidFontSize } from '@/components/v2/UtilityCss/utils';
import { ReactComponent as ArrowDownIcon } from '@/images/icons/icon-arrow-down-medium.svg';

import { ReactComponent as AppBuilderIllustration } from './illustrations/app-builder.svg';
import { ReactComponent as NotebooksIllustration } from './illustrations/notebooks.svg';
import { ReactComponent as SelfServeIllustration } from './illustrations/self-serve.svg';
import { config } from './config';
import { PlatformCapabilities } from './PlatformCapabilities';
import { DotGridMask } from './Textures';

const ArrowIndicatorCirc = styled.div`
  position: relative;
  height: 48px;
  width: 48px;
  display: grid;
  place-content: center;
  border-radius: 999px;
  background-color: ${({ theme }) => darken(0.025, theme.palette.fg100)};
  position: absolute;
  bottom: 16px;
  right: 16px;
  box-shadow:
    0 4px 16px -3px rgba(0 0 0 / 1),
    0 1px 4px -1px rgba(0 0 0 / 1);
  color: ${({ theme }) => theme.palette.fgLoud};
  transform: translateX(56px) rotate(0.25turn);
  opacity: 0;
  transition: opacity, transform;
  transition-duration: var(--hover-transition-duration);
  transition-timing-function: var(--hover-transition-timing-function);

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border: 1px dashed ${({ theme }) => theme.palette.fg1100};
    border-radius: inherit;
    animation: rotate 8s linear infinite;

    @keyframes rotate {
      from {
        transform: rotate(1turn);
      }
    }
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    display: none;
  }
`;

function ArrowIndicator() {
  return (
    <ArrowIndicatorCirc>
      <ArrowDownIcon />
    </ArrowIndicatorCirc>
  );
}

const MainGrid = styled(LayoutSubgrid)`
  border-top: 1px solid ${({ theme }) => theme.palette.fg700};
`;

const WorkflowGrid = styled(LayoutSubgrid)`
  display: grid;
  background-color: ${({ theme }) => theme.palette.fg100};
`;

const WorkflowItemsGrid = styled.div`
  grid-column: 2 / -2;
  grid-row: 1 / -1;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  max-width: 1200px;
  margin: 0 auto;
  gap: 0 32px;
  padding: 0 32px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    grid-template-columns: repeat(2, 1fr);
    padding: 0 16px;
  }
`;

const WorkflowItemsGridLines = styled.div`
  grid-column: 1 / -1;
  grid-row: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
`;

const WorkflowItems = styled.div`
  grid-column: 1 / -1;
  grid-row: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
`;

const WorkflowHRule = styled(LayoutSubgrid)`
  position: relative;
  height: 1px;
  grid-row: 1 / -1;
  margin-top: 160px;
  grid-column: 1 / -1;
  background-color: ${({ theme }) => theme.palette.fg600};
`;

const PerlinNoise = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0;
  background-image: var(--workflow-noise);
  background-size: 256px 256px;
  animation: translate-noise 4s linear infinite;
  transition: opacity;
  transition-duration: var(--hover-transition-duration);
  transition-timing-function: var(--hover-transition-timing-function);

  @keyframes translate-noise {
    from {
      background-position: 50% 0px;
    }
    to {
      background-position: 50% 256px;
    }
  }
`;

const BgUnhover = styled.div`
  position: absolute;
  inset: 0;
  background-color: ${({ theme }) => theme.palette.fg800};
  transition: opacity 0.1s linear;
`;

const GradientMask = styled.div`
  position: absolute;
  inset: 0;
  mask-image: linear-gradient(
    to bottom,
    rgba(255 255 255 / 0.8),
    rgba(255 255 255 / 0.1) 60%
  );
`;

export function DotGridHover() {
  return (
    <GradientMask>
      <DotGridMask>
        <BgUnhover />
        <PerlinNoise />
      </DotGridMask>
    </GradientMask>
  );
}

const Workflow = styled.a`
  --hover-transition-duration: 529.33ms;
  --hover-transition-timing-function: linear(
    0 0%,
    0.005892 1%,
    0.022227 2%,
    0.047151 3%,
    0.079006 4%,
    0.116321 5%,
    0.157797 6%,
    0.202293 7.000000000000001%,
    0.248818 8%,
    0.296516 9%,
    0.344652 10%,
    0.392606 11%,
    0.439857 12%,
    0.485977 13%,
    0.530617 14.000000000000002%,
    0.573504 15%,
    0.614426 16%,
    0.653228 17%,
    0.689804 18%,
    0.724091 19%,
    0.756062 20%,
    0.785721 21%,
    0.813097 22%,
    0.838242 23%,
    0.861223 24%,
    0.882123 25%,
    0.901035 26%,
    0.918061 27%,
    0.933305 28.000000000000004%,
    0.946879 28.999999999999996%,
    0.958894 30%,
    0.96946 31%,
    0.978689 32%,
    0.986687 33%,
    0.993561 34%,
    0.999411 35%,
    1.004333 36%,
    1.00842 37%,
    1.011758 38%,
    1.014427 39%,
    1.016506 40%,
    1.018063 41%,
    1.019165 42%,
    1.019871 43%,
    1.020237 44%,
    1.020313 45%,
    1.020144 46%,
    1.019771 47%,
    1.019232 48%,
    1.018558 49%,
    1.01778 50%,
    1.016922 51%,
    1.016007 52%,
    1.015055 53%,
    1.014083 54%,
    1.013105 55.00000000000001%,
    1.012133 56.00000000000001%,
    1.011177 56.99999999999999%,
    1.010246 57.99999999999999%,
    1.009346 59%,
    1.008483 60%,
    1.00766 61%,
    1.006882 62%,
    1.006148 63%,
    1.005462 64%,
    1.004822 65%,
    1.00423 66%,
    1.003683 67%,
    1.003182 68%,
    1.002725 69%,
    1.002309 70%,
    1.001933 71%,
    1.001595 72%,
    1.001293 73%,
    1.001024 74%,
    1.000786 75%,
    1.000578 76%,
    1.000396 77%,
    1.000238 78%,
    1.000103 79%,
    0.999989 80%
  );

  grid-column: span 2;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  padding: 24px;
  margin-top: 40px;
  border-bottom: 0;
  border-radius: 1px;
  border: 1px solid ${({ theme }) => gridColorSelector(theme, 'major')};
  background-color: ${({ theme }) => theme.palette.fg100};

  transition: border, transform, box-shadow, background-color;
  transition-duration: var(--hover-transition-duration);
  transition-timing-function: var(--hover-transition-timing-function);

  @media (min-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    &:hover,
    &:focus-visible {
      background-color: ${({ theme }) => darken(0.0125, theme.palette.fg100)};
      box-shadow:
        0px 8px 20px -4px rgba(0, 0, 0, 0.2),
        0px 4px 3px -1px rgba(0, 0, 0, 0.12);
      border-radius: 6px;
      border-color: var(--workflows-color);
      transform: translateY(-4px);

      ${PerlinNoise} {
        opacity: 1;
      }

      ${ArrowIndicatorCirc} {
        transform: none;
        opacity: 1;
      }
    }
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background-image: ${({ theme }) => theme.textures.noise.img};
    background-size: ${({ theme }) => theme.textures.noise.size};
    mix-blend-mode: ${({ theme }) => theme.textures.noise.mixBlendMode};
    opacity: ${({ theme }) => theme.textures.noise.opacity};
    pointer-events: none;
  }
`;

const WorkflowVRule = styled.div`
  width: 1px;
  background: ${({ theme }) => theme.palette.fg500};
  grid-row: 1 / -1;
  margin-top: 100px;

  &[data-i='1'] {
    grid-column: 2;
    justify-self: flex-start;
  }

  &[data-i='2'] {
    grid-column: 1 / -1;
    justify-self: center;
  }

  &[data-i='3'] {
    grid-column: -3;
    justify-self: flex-end;
  }
`;

const IllustrationContainer = styled.div`
  height: 110px;
  margin: 28px auto -32px auto;
  position: relative;
  display: flex;
  width: 100%;
  justify-content: center;
  isolation: isolate;
  z-index: 2;

  svg {
    height: 188px;
    position: absolute;
    bottom: 0;
    margin: 0 auto;
  }
`;

const Title = styled.div`
  width: min-content;
  display: flex;
  justify-content: center;
  gap: 8px;
`;
const H3 = styled.h3`
  color: currentColor;
  white-space: nowrap;
`;

const P = styled.p`
  font-weight: ${({ theme }) => theme.fontWeight.LIGHT};
  color: ${({ theme }) => theme.palette.fg1200};
`;

const Em = styled.em`
  color: ${({ theme }) => theme.palette.fgLoud};
  font-weight: ${({ theme }) => theme.fontWeight.BOLD};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 12px;
  max-width: 46ch;
`;

const SectionTitle = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: subgrid;
  grid-column: 2 / -2;
`;

const H2 = styled.h2`
  margin-bottom: 8px;
  text-align: center;
  max-width: 18ch;
  text-wrap: balance;
  font-size: ${generateFluidFontSize(28, 48)};
  margin: 0 auto;
`;

const Headlines = styled.div`
  position: relative;
  grid-column: 2 / -2;
  padding: 80px 16px 64px 16px;
  justify-items: center;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    grid-column: 1 / -1;
  }
`;

const OnePlatform = styled.div`
  position: relative;
  height: 56px;
  grid-column: 2 / -2;
  border-bottom: 1px solid ${({ theme }) => theme.palette.fg700};
  border-left: 1px solid ${({ theme }) => theme.palette.fg700};
  border-right: 1px solid ${({ theme }) => theme.palette.fg700};
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    grid-column: 1 / -1;
    margin: 0 32px;
  }
`;

const PlatformLabel = styled.div`
  position: absolute;
  padding: 4px 12px;
  white-space: nowrap;
  background-color: ${({ theme }) => darken(0.0, theme.palette.fg100)};
  border: 1px solid ${({ theme }) => gridColorSelector(theme, 'major')};
  color: ${({ theme }) => theme.palette.fg1100};
  font-size: ${({ theme }) => theme.fontSize.EXTRA_SMALL};
  left: 50%;
  bottom: 0;
  border-radius: 1px;
  transform: translate(-50%, 50%);

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background-image: ${({ theme }) => theme.textures.noise.img};
    background-size: ${({ theme }) => theme.textures.noise.size};
    mix-blend-mode: ${({ theme }) => theme.textures.noise.mixBlendMode};
    opacity: ${({ theme }) => theme.textures.noise.opacity};
    pointer-events: none;
  }
`;

const AccentL = styled.div`
  position: absolute;
  left: -1px;
  top: -1px;
  height: 6px;
  width: 6px;

  border-left: 1px solid ${({ theme }) => theme.palette.fg1200};
  border-top: 1px solid ${({ theme }) => theme.palette.fg1200};

  &[data-right] {
    border-left: none;
    border-right: 1px solid ${({ theme }) => theme.palette.fg1200};
    left: auto;
    right: -1px;
  }
`;

const AccentPlus = styled.div`
  grid-column: 2;
  color: ${({ theme }) => theme.palette.fg1200};
  margin-top: -3.5px;
  margin-left: -4.5px;
  height: 8px;
  width: 8px;
  display: grid;
  align-items: center;
  justify-items: center;
  justify-content: center;
  place-items: center;
  z-index: 1;

  &::before,
  &::after {
    content: '';
    display: block;
    background-color: currentColor;
    grid-area: 1 / -1;
  }

  &::before {
    width: 1px;
    height: 8px;
  }

  &::after {
    width: 8px;
    height: 1px;
  }

  &[data-right] {
    margin-left: -3.5px;
    grid-column: -2;
  }
`;

const Subheading = styled.p`
  font-weight: ${({ theme }) => theme.fontWeight.LIGHT};
  max-width: 54rem;
  text-align: center;
  margin: 0 auto;
`;

const PlatformCapabilitiesWrapper = styled.div`
  grid-column: 1 / -1;
  display: grid;
  justify-content: stretch;
  justify-items: center;

  &::before {
    content: '';
    width: 1px;
    border-right: 1px solid ${({ theme }) => theme.palette.fg500};
  }

  &::before,
  & > * {
    grid-row: 1 / -1;
    grid-column: 1 / -1;
    grid-area: 1 / -1;
  }
`;

export function Workflows() {
  return (
    <MainGrid>
      <WorkflowGrid>
        <ContentMarginLine />
        <SectionTitle>
          <AccentL />
          <AccentL data-right />
          <Headlines>
            <H2 className={headingStyles.h1}>
              One home for analytics, AI, and apps
            </H2>
            <Subheading className={textStyles.l}>
              Hex allows teams to work together across one platform, making it
              easy to find answers in your data, share them with stakeholders,
              and make decisions with confidence.
            </Subheading>
          </Headlines>
        </SectionTitle>
        <ContentMarginLine data-right />
        <LayoutSubgrid>
          <ContentMarginLine style={{ gridRow: '1 / -1' }} />
          <WorkflowHRule>
            <AccentPlus />
            <AccentPlus data-right />
          </WorkflowHRule>
          <WorkflowItemsGrid>
            <WorkflowItemsGridLines>
              <WorkflowVRule data-i="2" />
            </WorkflowItemsGridLines>
            <WorkflowItems>
              <Workflow
                href={`#${config.workflows.notebooks.anchor}`}
                style={{
                  '--workflows-color': rgba(
                    config.workflows.notebooks.color,
                    0.2
                  ),
                  '--workflow-noise': config.workflows.notebooks.noise
                }}>
                <DotGridHover />
                <IllustrationContainer>
                  <NotebooksIllustration />
                </IllustrationContainer>
                <Content>
                  <Title style={{ color: config.workflows.notebooks.color }}>
                    {config.workflows.notebooks.icon}
                    <H3 className={textStyles.annotation}>NOTEBOOKS</H3>
                  </Title>
                  <P className={textStyles.m}>
                    <Em>Deep-dive analysis:</Em> Explore the new, novel, and
                    gnarly in an AI-powered, multiplayer notebook where Python,
                    SQL, and{' '}
                    <span style={{ whiteSpace: 'nowrap' }}>
                      no-code live together.
                    </span>
                  </P>
                </Content>
                <ArrowIndicator />
              </Workflow>
              <Workflow
                href={`#${config.workflows.data_apps.anchor}`}
                style={{
                  '--workflows-color': rgba(
                    config.workflows.data_apps.color,
                    0.2
                  ),
                  '--workflow-noise': config.workflows.data_apps.noise
                }}>
                <DotGridHover />
                <IllustrationContainer>
                  <AppBuilderIllustration />
                </IllustrationContainer>
                <Content>
                  <Title style={{ color: config.workflows.data_apps.color }}>
                    {config.workflows.data_apps.icon}
                    <H3 className={textStyles.annotation}>DATA APPS</H3>
                  </Title>
                  <P className={textStyles.m}>
                    <Em>Publish and share:</Em> Turn analyses into beautiful
                    experiences for stakeholders to pull insights and provide
                    feedback.
                  </P>
                </Content>
                <ArrowIndicator />
              </Workflow>
              <Workflow
                href={`#${config.workflows.self_serve.anchor}`}
                style={{
                  '--workflows-color': rgba(
                    config.workflows.self_serve.color,
                    0.37
                  ),
                  '--workflow-noise': config.workflows.self_serve.noise
                }}>
                <DotGridHover />
                <IllustrationContainer>
                  <SelfServeIllustration />
                </IllustrationContainer>
                <Content>
                  <Title style={{ color: config.workflows.self_serve.color }}>
                    {config.workflows.self_serve.icon}
                    <H3 className={textStyles.annotation}>SELF-SERVE</H3>
                  </Title>
                  <P className={textStyles.m}>
                    <Em>Self serve exploration:</Em> Get answers quickly in a
                    point-and-click experience, on top of governed data and
                    semantic models.
                  </P>
                </Content>
                <ArrowIndicator />
              </Workflow>
              <OnePlatform>
                <PlatformLabel className={textStyles.annotation}>
                  UNDERLYING HEX PLATFORM
                </PlatformLabel>
              </OnePlatform>
            </WorkflowItems>
            <PlatformCapabilitiesWrapper>
              <PlatformCapabilities />
            </PlatformCapabilitiesWrapper>
          </WorkflowItemsGrid>
          <ContentMarginLine data-right style={{ gridRow: '1 / -1' }} />
        </LayoutSubgrid>
      </WorkflowGrid>
    </MainGrid>
  );
}
